import * as React from 'react'
import { Text, View } from 'react-native'
import styled from '@theme/styled-components'

import Select from 'react-select'
import { GroupBase, StylesConfig } from 'react-select'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

import theme from '@theme/Theme'

const PickerComponent = ({
  disabled = false,
  error,
  hint,
  isClearable,
  isSearchable = true, // on web, searchable by default
  onChange,
  options,
  placeholder,
  value,
  indicator = true,
}: PickerProps) => {
  const i18n = useI18n()

  const [, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  return (
    <Container disabled={disabled}>
      <Select
        key={`select-${!!value}-${disabled}`}
        noOptionsMessage={(_) => i18n.t('components.picker.noOption')}
        isDisabled={disabled}
        isClearable={isClearable}
        options={options}
        placeholder={placeholder}
        value={value}
        onChange={(val) => onChange(val || undefined)}
        styles={customStyles(isPhone, !!error, indicator)}
        isSearchable={isSearchable}
      />
      {(!!error || !!hint) && <Hint error={!!error}>{error || hint}</Hint>}
    </Container>
  )
}

export default PickerComponent

const customStyles: (
  isPhone: boolean,
  error: boolean,
  indicator: boolean
) => Partial<StylesConfig<PickerOption, false, GroupBase<PickerOption>>> = (isPhone, error, indicator) => ({
  control: (_, state) => ({
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: theme.colors.background,
    borderWidth: `${isPhone ? 0 : 2}px `,
    borderBottomWidth: isPhone ? (state.menuIsOpen ? 2 : 1) : 2,
    borderStyle: 'solid',
    borderColor: state.menuIsOpen
      ? theme.colors.primary
      : isPhone
      ? theme.colors.primaryFocused
      : theme.colors.secondaryActive,
    borderRadius: isPhone ? '0px' : '6px',
    outlineWidth: '0px',
    caretColor: theme.colors.primary,
    minHeight: isPhone ? '48px' : '56px',
    padding: '0px 8px 0px 10px',
    '&:hover': {
      cursor: 'pointer',
    },
  }),

  dropdownIndicator: (styles, state) => ({
    ...styles,
    color: error ? theme.colors.negate : theme.colors.primary,
    transform: state.isFocused ? 'rotate(0.5turn)' : undefined,
    display: indicator ? 'visible' : 'none',
  }),

  input: (styles) => ({
    ...styles,
  }),

  indicatorSeparator: () => ({ display: 'none' }),

  menu: (styles) => ({
    ...styles,
    margin: '0px',
    borderRadius: '0px',
    backgroundColor: theme.colors.cardDisabled,
    boxShadow: undefined,
  }),
  menuList: (styles) => ({ ...styles, padding: '0px', maxHeight: '225px' }),

  noOptionsMessage: (styles) => ({
    ...styles,
    ...theme.fonts.Body,
    fontSize: '16px',
    color: theme.colors.secondaryText,
  }),

  option: (_, state) => ({
    ...theme.fonts.Body,
    fontSize: '16px',
    padding: '8px 14px',
    backgroundColor: state.isSelected ? theme.colors.disable : undefined,
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.colors.disable,
    },
  }),

  placeholder: (styles) => ({
    ...styles,
    ...theme.fonts.Body,
    fontSize: '16px',
    color: theme.colors.secondaryText,
  }),

  valueContainer: (styles) => ({
    ...styles,
    padding: '0px',
    margin: '0px',
    ...theme.fonts.Body,
    fontSize: '16px',
  }),
})

const Container = styled(View)<{ disabled: boolean }>`
  ${(props) => props.disabled && 'opacity: 0.6;'}
`

const Hint = styled(Text)<{ error: boolean }>`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => (props.error ? props.theme.colors.negate : props.theme.colors.secondaryText)};
  margin-top: 8px;
`
